import { z } from 'zod'

export const employmentTypes = ['full-time', 'part-time', 'contract', 'internship'] as const
export const experienceLevels = ['entry', 'mid', 'senior', 'lead', 'executive'] as const
export const salaryPeriods = ['hourly', 'monthly', 'yearly'] as const
export const remotePolicies = ['onsite', 'hybrid', 'remote'] as const
export const jobStatuses = ['draft', 'published', 'paused', 'closed', 'archived'] as const

// Convert empty strings to undefined for optional string fields
const optionalId = z.preprocess(
  (v) => (v === '' ? undefined : v),
  z.string().optional()
)

// Convert NaN/empty to undefined for optional number fields
const optionalPositiveInt = z.preprocess(
  (v) => {
    if (v === '' || v === undefined || v === null) return undefined
    const n = Number(v)
    return isNaN(n) ? undefined : n
  },
  z.number().int().positive().optional()
)

const jobBaseSchema = z.object({
  title: z
    .string()
    .min(2, 'Title must be at least 2 characters')
    .max(200, 'Title must be less than 200 characters'),
  description: z
    .string()
    .min(50, 'Description must be at least 50 characters')
    .max(50000, 'Description must be less than 50000 characters'),
  requirements: z.preprocess((v) => (v === '' ? undefined : v), z.string().max(50000).optional()),
  benefits: z.preprocess((v) => (v === '' ? undefined : v), z.string().max(50000).optional()),
  departmentId: optionalId,
  locationId: optionalId,
  employmentType: z.enum(employmentTypes).default('full-time'),
  experienceLevel: z.preprocess(
    (v) => (v === '' ? undefined : v),
    z.enum(experienceLevels).optional()
  ),
  salaryMin: optionalPositiveInt,
  salaryMax: optionalPositiveInt,
  salaryCurrency: z.string().length(3).default('USD'),
  salaryPeriod: z.enum(salaryPeriods).default('yearly'),
  showSalary: z.boolean().default(false),
  remotePolicy: z.enum(remotePolicies).default('onsite'),
  hiringManagerId: optionalId,
  pipelineTemplateId: z.string().optional(),
  closesAt: optionalPositiveInt,
})

const salaryRefine = <T extends { salaryMin?: number; salaryMax?: number }>(data: T) => {
  if (data.salaryMin && data.salaryMax) {
    return data.salaryMin <= data.salaryMax
  }
  return true
}

export const createJobSchema = jobBaseSchema.refine(salaryRefine, {
  message: 'Minimum salary must be less than or equal to maximum salary',
  path: ['salaryMin'],
})

export const updateJobSchema = jobBaseSchema.partial().extend({
  status: z.enum(jobStatuses).optional(),
}).refine(salaryRefine, {
  message: 'Minimum salary must be less than or equal to maximum salary',
  path: ['salaryMin'],
})

export const jobFiltersSchema = z.object({
  status: z.enum(jobStatuses).optional(),
  departmentId: z.string().optional(),
  locationId: z.string().optional(),
  employmentType: z.enum(employmentTypes).optional(),
  search: z.string().max(200).optional(),
})

export const createPipelineStageSchema = z.object({
  name: z.string().min(1).max(100),
  type: z.enum(['applied', 'screening', 'interview', 'offer', 'hired', 'rejected']),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/).default('#6b7280'),
  orderIndex: z.number().int().min(0),
  isTerminal: z.boolean().default(false),
  autoRejectDays: z.number().int().positive().optional(),
})

export const updatePipelineSchema = z.object({
  stages: z.array(createPipelineStageSchema).min(1),
})

export const createDepartmentSchema = z.object({
  name: z.string().min(1).max(100),
  parentId: z.string().optional(),
})

export const createLocationSchema = z.object({
  name: z.string().min(1).max(200),
  type: z.enum(['office', 'remote', 'hybrid']).default('office'),
  timezone: z.string().optional(),
})

export type CreateJobInput = z.infer<typeof createJobSchema>
export type UpdateJobInput = z.infer<typeof updateJobSchema>
export type JobFiltersInput = z.infer<typeof jobFiltersSchema>
export type CreatePipelineStageInput = z.infer<typeof createPipelineStageSchema>
export type UpdatePipelineInput = z.infer<typeof updatePipelineSchema>
export const updateDepartmentSchema = createDepartmentSchema.partial()

export const updateLocationSchema = createLocationSchema.partial()

export type CreateDepartmentInput = z.infer<typeof createDepartmentSchema>
export type UpdateDepartmentInput = z.infer<typeof updateDepartmentSchema>
export type CreateLocationInput = z.infer<typeof createLocationSchema>
export type UpdateLocationInput = z.infer<typeof updateLocationSchema>
